/**
 * ROI & Financial Impact Zod Schemas (F03)
 *
 * Type-safe schemas for kpi_snapshots and interventions tables.
 *
 * @module services/schemas/roiSchemas
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const PeriodTypeSchema = z.enum(['daily', 'weekly', 'monthly', 'quarterly']);
export type PeriodType = z.infer<typeof PeriodTypeSchema>;

export const InterventionTypeSchema = z.enum([
  'investigation', 'audit', 'policy_change', 'termination', 'training', 'recovery',
]);
export type InterventionType = z.infer<typeof InterventionTypeSchema>;

// ============================================
// HELPERS
// ============================================

/** Lenient UUID — accepts any UUID-formatted string (test UUIDs may not be RFC 4122 compliant) */
const dbUuid = z.string().regex(
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
);

const numericString = z.union([z.number(), z.string()]).nullable().transform(v =>
  v === null || v === undefined ? 0 : Number(v)
);

// ============================================
// KPI SNAPSHOT METRICS (JSONB shape)
// ============================================

export const KPISnapshotMetricsSchema = z.object({
  shrinkage_rate: z.number().default(0),
  ira: z.number().default(0),
  exception_count: z.number().int().default(0),
  total_loss_value: z.number().default(0),
  resolved_cases: z.number().int().default(0),
  recovery_value: z.number().default(0),
  sales_total: z.number().default(0),
  inventory_value: z.number().default(0),
  cri_score: z.number().default(0),
}).passthrough();
export type KPISnapshotMetrics = z.infer<typeof KPISnapshotMetricsSchema>;

/** Long-format view of one numeric entry of a kpi_snapshots `metrics` object. */
export interface KPISnapshotMetricPoint {
  metric_name: string;
  metric_value: number;
  snapshot_date: string;
}

/**
 * kpi_snapshots stores one wide `metrics` JSONB object per row; flatten it to
 * one (metric_name, metric_value) point per numeric entry for time-series use.
 */
export function flattenKpiSnapshotMetrics(
  rows: Array<{ snapshot_date: string; metrics: unknown }>
): KPISnapshotMetricPoint[] {
  const points: KPISnapshotMetricPoint[] = [];
  for (const row of rows) {
    if (!row.metrics || typeof row.metrics !== 'object') continue;
    for (const [metric_name, raw] of Object.entries(row.metrics as Record<string, unknown>)) {
      if (raw === null || raw === '' || typeof raw === 'boolean') continue;
      const metric_value = Number(raw);
      if (Number.isFinite(metric_value)) points.push({ metric_name, metric_value, snapshot_date: row.snapshot_date });
    }
  }
  return points;
}

// ============================================
// DB ROW SCHEMAS
// ============================================

export const KPISnapshotDbSchema = z.object({
  id: dbUuid,
  tenant_id: dbUuid,
  store_id: dbUuid,
  snapshot_date: z.string(),
  period_type: PeriodTypeSchema,
  metrics: z.any(),
  created_at: z.string(),
});
export type KPISnapshotRow = z.infer<typeof KPISnapshotDbSchema>;

export const InterventionDbSchema = z.object({
  id: dbUuid,
  tenant_id: dbUuid,
  store_id: dbUuid,
  intervention_type: InterventionTypeSchema,
  related_case_id: dbUuid.nullable(),
  related_alert_id: dbUuid.nullable(),
  financial_impact: numericString,
  description: z.string().nullable(),
  performed_by: dbUuid.nullable(),
  performed_at: z.string(),
});
export type InterventionRow = z.infer<typeof InterventionDbSchema>;

// ============================================
// INPUT SCHEMAS
// ============================================

export const InterventionInputSchema = z.object({
  store_id: dbUuid,
  intervention_type: InterventionTypeSchema,
  related_case_id: dbUuid.optional(),
  related_alert_id: dbUuid.optional(),
  financial_impact: z.number(),
  description: z.string().min(1, 'Description required'),
  performed_by: dbUuid.optional(),
});
export type InterventionInput = z.infer<typeof InterventionInputSchema>;

export const KPISnapshotFiltersSchema = z.object({
  storeId: dbUuid.optional(),
  periodType: PeriodTypeSchema.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export type KPISnapshotFilters = z.infer<typeof KPISnapshotFiltersSchema>;

export const InterventionFiltersSchema = z.object({
  storeId: dbUuid.optional(),
  interventionType: InterventionTypeSchema.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
});
export type InterventionFilters = z.infer<typeof InterventionFiltersSchema>;

// ============================================
// OUTPUT / VIEW TYPES
// ============================================

export interface ROISummary {
  totalIdentifiedLoss: number;
  totalRecovered: number;
  netSavings: number;
  roiPercent: number;
  interventionCount: number;
  period: { from: string; to: string };
}

export interface PeriodComparison {
  metric: string;
  period1Value: number;
  period2Value: number;
  delta: number;
  deltaPercent: number;
  direction: 'improved' | 'declined' | 'stable';
}

export interface InterventionPage {
  data: InterventionRow[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================
// LABELS (Spanish)
// ============================================

export const PERIOD_TYPE_LABELS: Record<PeriodType, string> = {
  daily: 'Diario',
  weekly: 'Semanal',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
};

export const INTERVENTION_TYPE_LABELS: Record<InterventionType, string> = {
  investigation: 'Investigación',
  audit: 'Auditoría',
  policy_change: 'Cambio de Política',
  termination: 'Desvinculación',
  training: 'Capacitación',
  recovery: 'Recuperación',
};
